import { useCallback, useEffect, useState } from 'react';

import { useBridgewayApi } from '@/api/BridgewayApi';
import { useUserId } from '@/auth/UserIdContext';
import { logger } from '@/logger';

// One agent ask waiting on the owner. The strategy agent's output is always a proposal and
// never an automatic edit — which only means something if the owner can find it. This is the
// finding: every pending proposal across every strategy, newest first.
export interface RecommendedStep {
	action_kind: string;
	description: string;
	campaign_id?: string | null;
	campaign_title?: string | null;
	step_index?: number | null;
	target_id?: string | null;
	applier: string;
	applier_params?: Record<string, unknown>;
	sequence_note?: string | null;
	depends_on?: string | null;
}

export interface PendingProposal {
	id: string;
	strategy_id: string;
	reading_id: string;
	change_type:
		| 'adjust_metric_target'
		| 'reshape_campaign'
		| 'add_follow_on'
		| 'propose_new_strategy'
		| 'activate_campaigns'
		| 'recommend_next_step';
	target_id?: string | null;
	target_ids?: string[];
	proposed_target?: number | null;
	recommended_step?: RecommendedStep | null;
	rationale: string;
	status: 'pending' | 'approved' | 'rejected';
	created_at?: string;
}

export interface UsePendingProposals {
	proposals: PendingProposal[];
	loading: boolean;
	error: string | null;
	refresh: () => Promise<void>;
	respond: (
		proposalId: string,
		decision: 'approve' | 'reject',
	) => Promise<void>;
}

// Refetches on `revision`, which the caller passes through from `strategy_dock_last_updated`. The
// agent writes proposals from a background tick, and every write that raises one also touches
// the strategy doc — so the counter that already tells the dock its list is stale tells this
// hook the same thing, and no second push channel is needed.
export function usePendingProposals(
	opts: { enabled?: boolean; revision?: number } = {},
): UsePendingProposals {
	const { enabled = true, revision } = opts;
	const userId = useUserId();
	const bridgewayApi = useBridgewayApi();
	const [proposals, setProposals] = useState<PendingProposal[]>([]);
	const [loading, setLoading] = useState(enabled);
	const [error, setError] = useState<string | null>(null);

	const refresh = useCallback(async () => {
		if (!userId || !enabled) return;
		try {
			const rows = await bridgewayApi.get<PendingProposal[]>(
				`/api/user/${userId}/strategy-reviews/pending-proposals`,
			);
			setProposals(Array.isArray(rows) ? rows : []);
			setError(null);
		} catch (err) {
			// Surfaced, never swallowed into an empty list: "nothing is waiting on you" and
			// "we could not find out" look identical to an owner and mean opposite things.
			logger.error('Failed to load pending proposals', err);
			setError("We couldn't load what's waiting for you.");
		} finally {
			setLoading(false);
		}
	}, [userId, enabled, bridgewayApi]);

	useEffect(() => {
		void refresh();
	}, [refresh, revision]);

	const respond = useCallback(
		async (proposalId: string, decision: 'approve' | 'reject') => {
			if (!userId) return;
			await bridgewayApi.post(
				`/api/user/${userId}/strategy-reviews/proposals/${proposalId}/respond`,
				{ decision },
			);
			await refresh();
		},
		[userId, bridgewayApi, refresh],
	);

	return { proposals, loading, error, refresh, respond };
}

// What the owner is actually being asked, in their terms. The rationale explains why; this is
// the ask itself, and it has to be readable without opening anything.
export function proposalHeadline(p: PendingProposal): string {
	switch (p.change_type) {
		case 'activate_campaigns': {
			const n = p.target_ids?.length ?? 0;
			return n === 1 ? 'Start a campaign?' : `Start ${n} campaigns?`;
		}
		case 'adjust_metric_target':
			return p.proposed_target != null
				? `Change the target to ${p.proposed_target}?`
				: 'Change a target?';
		case 'reshape_campaign':
			return 'Reshape a campaign?';
		case 'add_follow_on':
			return 'Add a follow-on campaign?';
		case 'propose_new_strategy':
			return 'Start a new strategy?';
		case 'recommend_next_step': {
			const rs = p.recommended_step;
			if (!rs) return 'Recommended next step';
			switch (rs.action_kind) {
				case 'activate_campaign':
					return `Ready to start: ${rs.campaign_title || 'a campaign'}`;
				case 'owner_task':
					return `Action needed: ${(rs.description ?? '').slice(0, 60)}`;
				case 'adjust_target':
					return 'Suggested target change';
				case 'create_content':
					return 'Ready to create new content';
				case 'create_monitor':
					return 'Set up monitoring?';
				case 'add_follow_on_campaign':
					return `Add follow-on: ${rs.campaign_title || 'a new campaign'}`;
				case 'conclude_strategy':
					return 'Ready to wrap up?';
				case 'propose_new_strategy':
					return 'Start a new strategy based on what we learned?';
				default:
					return 'Recommended next step';
			}
		}
		default:
			return 'Something needs your decision';
	}
}
